import type { History } from "history"
import moment, { type Moment } from "moment-timezone"
import type { NavigateFunction, NavigateOptions } from "react-router-dom"

import type { IIndexable } from "@common-shared/types/general.types.ts"
import { UtilHelper as CommonUtilHelper } from "@common-shared/helpers/util.helper"
import type { IAddress } from "@common-shared/types/responseData/commonResponseData.types.ts"

import { FRONT_GENERAL_CONFIG } from "../config/general.config"


export class UtilHelper extends CommonUtilHelper {
    /**
     * Check if object is of some type
     */
    static isObjectOfType<Type>(
        item: any,
        propertyCheck: keyof Type,
    ): item is Type {
        return UtilHelper.isObject(item) && propertyCheck in item
    }

    /**
     * Check if objects are equal
     */
    static objectsEqual(obj1: object, obj2: object): boolean {
        return JSON.stringify(obj1) === JSON.stringify(obj2)
    }

    /**
     * Deep copy object
     */
    static deepCopyObject(obj: object): any {
        return JSON.parse(JSON.stringify(obj))
    }

    /**
     * Redirect to some path or another url
     */
    static redirectTo(
        path: string,
        navigate?: NavigateFunction,
        navigateOptions?: NavigateOptions,
    ) {
        setTimeout(() => {
            if (navigate) {
                void navigate(path, navigateOptions)
            } else {
                window.location.href = path
            }
        })
    }

    /**
     * Convert to mysql date format: YYYY-MM-DD
     */
    static dateToMysqlFormat(date: Date): string {
        return moment(date).format(FRONT_GENERAL_CONFIG.mysqlMomentDateFormat)
    }

    /**
     * Convert date obj or timestamp to UTC timezone
     */
    static dateToUTC(date: Date | number): Date | number {
        let localDate = date

        if (!(date instanceof Date)) {
            localDate = new Date(date)
        }

        const mom = moment.tz(
            UtilHelper.dateToMysqlFormat(localDate as Date),
            "YYYY-MM-DD",
            "UTC",
        )
        const utcTimestamp = mom.unix() * 1000

        return date instanceof Date ? new Date(utcTimestamp) : utcTimestamp
    }

    /**
     * Convert date obj or timestamp from UTC (comes from server) to browser tz
     */
    static dateFromUTC(date: Date | number): Date | number {
        let localDate = date

        if (!(date instanceof Date)) {
            localDate = new Date(date)
        }

        let mom = moment.tz(
            UtilHelper.dateToMysqlFormat(localDate as Date),
            "YYYY-MM-DD",
            "UTC",
        )
        mom = mom.tz(moment.tz.guess())

        const userTimestamp = mom.unix() * 1000

        return date instanceof Date ? new Date(userTimestamp) : userTimestamp
    }

    /**
     * Get amount of cents, so basically amount after dot (2344 -> "44", 2304 -> "04")
     */
    static getCentsAmount(amount: number): string {
        const amountOfCents = Math.round(amount) % 100

        return amountOfCents < 10
            ? `0${amountOfCents}`
            : amountOfCents.toString()
    }

    /**
     * Format number in locale fashion, with comma delimiters for thousands for example
     */
    static getFormattedLocaleNumber(num: number): string {
        return num.toLocaleString("en-US")
    }

    /**
     * Scroll to top of the page
     */
    static scrollToPageTop() {
        // Hack to scroll to top of page
        // Needed because we can navigate to these pages with window scrolled - so it's glitchy without it
        // In catch so it doesn't fail where not supported...

        try {
            window.scrollTo(0, 0)
        } catch (e) {}
    }

    /**
     * Get url search string param
     */
    static getRawUrlSearchParam(
        name: string,
        url = "",
        removeFromUrl = false,
        history?: History,
    ): string | undefined {
        try {
            url = url || window.location.href
            name = name.replace(/[[\]]/g, "\\$&")

            const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
            const results = regex.exec(url)

            if (!results) {
                return undefined
            }

            if (!results[2]) {
                return ""
            }

            const result = decodeURIComponent(results[2])

            if (result && removeFromUrl && history) {
                try {
                    history.replace({
                        search: "",
                    })
                } catch (e) {}
            }

            return result
        } catch (e) {
            return undefined
        }
    }

    /**
     * Add url query param to current url
     */
    static addUrlQueryParam(key: string, value: string) {
        const url = new URL(window.location.href)

        url.searchParams.set(key, value)
        window.history.pushState({}, "", url.toString())
    }

    /**
     * Convert file to base 64 format
     */
    static async fileToBase64(file: File) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()

            reader.readAsDataURL(file)

            reader.onload = () => {
                resolve(reader.result)
            }
            reader.onerror = error => {
                reject(error as unknown as Error)
            }
        })
    }

    /**
     * Get formatted address
     */
    static getFormattedAddress(address: IAddress | null): string {
        if (!address) {
            return "N/A"
        }

        return (
            (address.street ? address.street + ", " : "") +
            (address.street2 ? address.street2 + ", " : "") +
            (address.city ? address.city + ", " : "") +
            (address.state ? address.state + " " : "") +
            (address.zip || "")
        )
    }

    /**
     * Capitalize first letter in string
     */
    static capitalizeFirstLetter(item?: string): string {
        if (!item) {
            return ""
        }

        return item.charAt(0).toUpperCase() + item.slice(1)
    }

    /**
     * Get current url of app
     */
    static getCurrentAppDomain(): string {
        return (
            window.location.protocol +
            "//" +
            window.location.hostname +
            (window.location.port ? ":" + window.location.port : "")
        )
    }

    /**
     * Get current full url
     */
    static getCurrentUrl(withSearch = false): string {
        return (
            UtilHelper.getCurrentAppDomain() +
            (window.location.pathname && window.location.pathname !== "/"
                ? window.location.pathname
                : "") +
            (withSearch && window.location.search ? window.location.search : "")
        )
    }

    /**
     * Get periodic change in percentages for 2 values
     */
    static getPeriodicChangePercentage(
        currentValue?: number,
        previousPeriodValue?: number,
    ): number {
        if (
            typeof currentValue === "undefined" ||
            typeof previousPeriodValue === "undefined"
        ) {
            return 0
        }

        try {
            const result =
                (currentValue > previousPeriodValue ? 1 : -1) *
                ((100 *
                    (Math.max(currentValue, previousPeriodValue) -
                        Math.min(currentValue, previousPeriodValue))) /
                    Math.max(currentValue, previousPeriodValue))

            if (isNaN(result)) {
                return 0
            }

            return result
        } catch (e) {
            return 0
        }
    }

    /**
     * Check how many days from now to timestamp
     */
    static daysUntilTimestamp(timestamp: number, isUtc = true): number {
        try {
            let momentTimestamp = moment(timestamp)

            if (isUtc) {
                momentTimestamp = momentTimestamp.utc()
            }

            return momentTimestamp.diff(moment(), "day")
        } catch (e) {
            return 0
        }
    }

    /**
     * Check if date is valid
     */
    static isValidDate(date: number | null | Date): boolean {
        try {
            if (!(date instanceof Date) && typeof date === "number") {
                date = new Date(date)
            }

            if (!date) {
                return false
            }

            return !isNaN(date.getTime())
        } catch (e) {
            return false
        }
    }

    /**
     * Open url in new tab
     */
    static openUrlInNewTab(url: string) {
        window.open(url, "_blank")?.focus()
    }

    /**
     * Merge 2 arrays
     * Make sure there are no duplicates in resulting array by ids
     */
    static mergeArrayByUniqueKey(
        items: IIndexable[],
        newItems: IIndexable[],
        uniqueKey = "id",
    ): IIndexable[] {
        const existingItemsKeys = items.map(item => item[uniqueKey])

        return [
            ...items,
            ...newItems.filter(
                item => !existingItemsKeys.includes(item[uniqueKey]),
            ),
        ]
    }

    /**
     * Get random integer
     */
    static getRandomInt(min = 0, max = 1000000): number {
        min = Math.ceil(min)
        max = Math.floor(max)

        return Math.floor(Math.random() * (max - min)) + min //The maximum is exclusive and the minimum is inclusive
    }

    /**
     * Remove plus signs from email, so same email can't be reused....
     */
    static getPlusSignFreeEmail(email: string): string {
        return email.split("@")[0].split("+")[0] + "@" + email.split("@")[1]
    }

    /**
     * Get real indexes of some array
     * In some cases where you use "delete" on array item indexes can be messed up
     * So we'd like to get real indexes here by filtering out what's considered "empty" items
     */
    static getRealIndexesOfArray(arr: any[]): number[] {
        return arr
            .map((val, i) => !!val && i)
            .filter(item => typeof item === "number")
    }

    /**
     * Append query param to url and get new url
     */
    static appendQueryParams(url: string, data: IIndexable = {}): string {
        const keys = Object.keys(data)

        if (keys.length) {
            url += "?"
        }

        const params = [] as string[]

        // https://stackoverflow.com/questions/7842547/request-parameter-losing-plus-sign
        keys.forEach(key => {
            if (typeof data[key] !== "undefined") {
                params.push(`${key}=${encodeURIComponent(data[key])}`)
            }
        })

        return url + params.join("&")
    }

    /**
     * Build user abbreviation as first letter of first and last name
     */
    static buildUserAbbreviation(
        user?: {
            name?: string | null
            firstName?: string | null
            lastName?: string | null
        } | null,
    ): string {
        if (user?.name) {
            const parts = user.name.split(" ")
            parts.length = 2

            return parts
                .map((item: string) => item.charAt(0).toUpperCase())
                .join("")
        }

        if (user?.firstName || user?.lastName) {
            return [user.firstName, user.lastName]
                .map((item: string | null | undefined) =>
                    (item || "").charAt(0).toUpperCase(),
                )
                .join("")
        }

        return "🧑‍⚕️"
    }

    /**
     * Check different formats of date string
     */
    static getPossibleDateField(
        value?: string,
        failOnAllInvalid = false,
    ): {
        primaryFormat?: Moment
        secondaryFormat?: Moment
        thirdFormat?: Moment
        valid?: Moment
    } {
        if (!value) {
            return {
                primaryFormat: undefined,
                secondaryFormat: undefined,
                thirdFormat: undefined,
                valid: undefined,
            }
        }

        const primaryFormat = moment(
            value,
            FRONT_GENERAL_CONFIG.defaultMomentDateFormat,
            true,
        )

        const secondaryFormat = moment(
            value,
            FRONT_GENERAL_CONFIG.mysqlMomentDateFormat,
            true,
        )

        const thirdFormat = moment(
            value,
            FRONT_GENERAL_CONFIG.defaultMomentDateFormatShortYear,
            true,
        )

        if (
            failOnAllInvalid &&
            !primaryFormat?.isValid() &&
            !secondaryFormat?.isValid() &&
            !thirdFormat?.isValid()
        ) {
            throw new Error("All date formats are invalid")
        }

        return {
            primaryFormat,
            secondaryFormat,
            thirdFormat,
            valid: [primaryFormat, secondaryFormat, thirdFormat].find(
                format => {
                    return format?.isValid()
                },
            ),
        }
    }
}
